import { OPTIMISTIC_ID_PREFIX, isHumanUserMessage } from '../utils/chatHelpers';
import { mergeLateDisplayBlocks } from '../utils/mergeLateDisplayBlocks';
import { DELTA_ID_PREFIX } from './types';
/**
 * Preserve stableId when the optimistic→settled swap arrives via a bulk snapshot
 * (`MESSAGE_REPLACE_ALL` / `MESSAGE_RECONCILE_ALL`) rather than
 * `MESSAGE_REPLACE_OPTIMISTIC`. Pairs each dropped optimistic message with its
 * settled counterpart — a genuinely-new incoming id of the same role, in send
 * order — so normalize keeps the turn's key constant. `prevStableMap` is
 * returned by reference when nothing pairs.
 */
function mapOptimisticStableIds(prevStableMap, prevMessages, incoming) {
  const droppedOptimistic = prevMessages.filter(m => m.id.startsWith(OPTIMISTIC_ID_PREFIX));
  if (droppedOptimistic.length === 0) return prevStableMap;
  const keptIds = new Set(prevMessages.filter(m => !m.id.startsWith(OPTIMISTIC_ID_PREFIX)).map(m => m.id));
  const newIncoming = incoming.filter(m => !keptIds.has(m.id));
  if (newIncoming.length === 0) return prevStableMap;
  let stableMap = prevStableMap;
  const usedIndexes = new Set();
  for (const optimistic of droppedOptimistic) {
    var _prevStableMap$get;
    const matchIndex = newIncoming.findIndex((candidate, index) => !usedIndexes.has(index) && candidate.role === optimistic.role);
    if (matchIndex === -1) continue;
    usedIndexes.add(matchIndex);
    if (stableMap === prevStableMap) {
      stableMap = new Map(prevStableMap);
    }
    stableMap.set(newIncoming[matchIndex].id, (_prevStableMap$get = prevStableMap.get(optimistic.id)) !== null && _prevStableMap$get !== void 0 ? _prevStableMap$get : optimistic.id);
  }
  return stableMap;
}
export function reduceMessages(state, action) {
  switch (action.type) {
    case 'MESSAGE_APPEND':
      {
        const existingIndex = state.messages.findIndex(msg => msg.id === action.message.id);
        if (existingIndex !== -1) {
          // A duplicate-id append can carry DISPLAY_ONLY blocks the server tacked
          // on after the message first streamed in (e.g. TOOL_APPROVAL_REQUEST on
          // a paused turn). Merge only those new blocks in; a null result means
          // there's nothing new, so keep the original no-op.
          const mergedMessage = mergeLateDisplayBlocks(state.messages[existingIndex], action.message);
          if (mergedMessage === null) return state;
          const merged = [...state.messages];
          merged[existingIndex] = mergedMessage;
          return Object.assign({}, state, {
            messages: merged
          });
        }
        const deltaIds = [];
        const base = [];
        for (const msg of state.messages) {
          if (msg.id.startsWith(DELTA_ID_PREFIX)) {
            deltaIds.push(msg.id);
          } else {
            base.push(msg);
          }
        }
        const hasDelta = deltaIds.length > 0;
        if (!hasDelta && state.turnId === null) {
          return Object.assign({}, state, {
            messages: [...state.messages, action.message]
          });
        }
        const appendMap = new Map(state.messageTurnIdMap);
        if (action.keepDeltas) {
          // Cancellation interrupt: keep the partial streamed text visible and
          // append after it. The next TURN_STARTED sweeps the stale deltas.
          if (state.turnId !== null) {
            appendMap.set(action.message.id, state.turnId);
          }
          return Object.assign({}, state, {
            messages: [...state.messages, action.message],
            messageTurnIdMap: appendMap
          });
        }
        for (const deltaId of deltaIds) {
          appendMap.delete(deltaId);
        }
        if (state.turnId !== null) {
          appendMap.set(action.message.id, state.turnId);
        }
        // Preserve the streamed message's identity across the delta→settle swap:
        // map the settled backend id to the delta id it replaced so normalize can
        // derive a stableId that stayed constant during streaming. Exactly one
        // delta message is active at a time; the most recent is the one settling.
        let stableMap = state.messageStableIdMap;
        if (hasDelta) {
          stableMap = new Map(state.messageStableIdMap);
          stableMap.set(action.message.id, deltaIds[deltaIds.length - 1]);
        }
        return Object.assign({}, state, {
          messages: [...(hasDelta ? base : state.messages), action.message],
          messageTurnIdMap: appendMap,
          messageStableIdMap: stableMap
        });
      }
    case 'TURN_STARTED':
      {
        // Sweep deltas left over from a cancelled turn before the next one streams.
        const cleaned = state.messages.filter(msg => !msg.id.startsWith(DELTA_ID_PREFIX));
        if (cleaned.length === state.messages.length) return state;
        const cleanedMap = new Map(state.messageTurnIdMap);
        for (const msg of state.messages) {
          if (msg.id.startsWith(DELTA_ID_PREFIX)) cleanedMap.delete(msg.id);
        }
        return Object.assign({}, state, {
          messages: cleaned,
          messageTurnIdMap: cleanedMap
        });
      }
    case 'MESSAGE_DELTA_UPDATE':
      {
        const existingIndex = state.messages.findIndex(msg => msg.id === action.message.id);
        const needsMapUpdate = state.turnId !== null && state.messageTurnIdMap.get(action.message.id) !== state.turnId;
        const deltaMap = needsMapUpdate ? new Map(state.messageTurnIdMap) : state.messageTurnIdMap;
        if (needsMapUpdate) {
          deltaMap.set(action.message.id, state.turnId);
        }
        if (existingIndex >= 0) {
          const updated = [...state.messages];
          updated[existingIndex] = action.message;
          return Object.assign({}, state, {
            messages: updated,
            messageTurnIdMap: deltaMap
          });
        }
        return Object.assign({}, state, {
          messages: [...state.messages, action.message],
          messageTurnIdMap: deltaMap
        });
      }
    case 'MESSAGE_REPLACE_OPTIMISTIC':
      {
        const optimistic = state.messages[state.messages.length - 1];
        const replaceMap = new Map(state.messageTurnIdMap);
        if (optimistic) {
          replaceMap.delete(optimistic.id);
        }
        if (state.turnId !== null) {
          replaceMap.set(action.message.id, state.turnId);
        }
        // Preserve the message's identity across the optimistic→settled swap, the
        // same way the delta→settle path does: map the settled backend id to the
        // optimistic id it replaced so normalize derives a stableId that stayed
        // constant from send. Without this the human turn's stableId flips to the
        // backend id mid-turn, breaking anything keyed on it (see usePreflightStatusByTurn).
        let stableMap = state.messageStableIdMap;
        if (optimistic !== null && optimistic !== void 0 && optimistic.id.startsWith(OPTIMISTIC_ID_PREFIX)) {
          var _state$messageStableI;
          stableMap = new Map(state.messageStableIdMap);
          stableMap.set(action.message.id, (_state$messageStableI = state.messageStableIdMap.get(optimistic.id)) !== null && _state$messageStableI !== void 0 ? _state$messageStableI : optimistic.id);
        }
        return Object.assign({}, state, {
          messages: [...state.messages.slice(0, -1), action.message],
          messageTurnIdMap: replaceMap,
          messageStableIdMap: stableMap
        });
      }
    case 'MESSAGE_REPLACE_ALL':
      {
        // Incremental append: the polling transport delivers only the messages
        // after its `afterMessageId` cursor, so `action.messages` is NOT a full
        // turn — it must be appended, never used to reorder. (The authoritative
        // full-snapshot path is MESSAGE_RECONCILE_ALL.)
        const kept = state.messages.filter(m => !m.id.startsWith(OPTIMISTIC_ID_PREFIX));
        const existingIds = new Set(kept.map(m => m.id));
        const newOnly = action.messages.filter(m => !existingIds.has(m.id));
        const stableMap = mapOptimisticStableIds(state.messageStableIdMap, state.messages, action.messages);
        if (state.turnId === null || newOnly.length === 0) {
          return Object.assign({}, state, {
            messages: [...kept, ...newOnly],
            messageStableIdMap: stableMap
          });
        }
        const replaceAllMap = new Map(state.messageTurnIdMap);
        for (const msg of newOnly) {
          replaceAllMap.set(msg.id, state.turnId);
        }
        return Object.assign({}, state, {
          messages: [...kept, ...newOnly],
          messageTurnIdMap: replaceAllMap,
          messageStableIdMap: stableMap
        });
      }
    case 'MESSAGE_RECONCILE_ALL':
      {
        // getTurnResult returns only the CURRENT turn, including the server-id
        // version of the current turn's user message. That supersedes the current
        // turn's optimistic user message — the last optimistic message in the
        // list — so drop only that one. Optimistic user messages from PRIOR turns
        // (sent in-session and never swapped to a server id) are absent from this
        // single-turn snapshot, so they must be retained like any other
        // prior-turn message rather than stripped wholesale.
        //
        // Only supersede when the snapshot brings a human user echo not already in
        // state. Two traps this avoids: a hidden (skipUserEcho) turn carries no
        // echo (and tool-result relays are role USER too, so match the echo with
        // isHumanUserMessage, not role alone); and a LATER reconcile of the same
        // turn re-supplies an echo already present in state, at which point the
        // current turn has no optimistic left — the trailing scan would then land
        // on a PRIOR turn's still-optimistic user and drop its bubble.
        const snapshotUserEcho = action.messages.find(msg => isHumanUserMessage(msg) && !state.messages.some(existing => existing.id === msg.id));
        let currentTurnOptimisticId = null;
        if (snapshotUserEcho !== undefined) {
          for (let i = state.messages.length - 1; i >= 0; i--) {
            if (state.messages[i].id.startsWith(OPTIMISTIC_ID_PREFIX)) {
              currentTurnOptimisticId = state.messages[i].id;
              break;
            }
          }
        }
        const retained = state.messages.filter(m => m.id !== currentTurnOptimisticId);
        // Rebuild in snapshot order, reusing existing instances by id so streamed
        // identity/stableId is preserved.
        const retainedById = new Map(retained.map(m => [m.id, m]));
        const ordered = action.messages.map(m => {
          var _retainedById$get;
          return (_retainedById$get = retainedById.get(m.id)) !== null && _retainedById$get !== void 0 ? _retainedById$get : m;
        });
        const orderedIds = new Set(action.messages.map(m => m.id));
        const notInSnapshot = retained.filter(m => !orderedIds.has(m.id));
        // Preserve the superseded optimistic user message's identity: map the
        // snapshot's server-id user echo to the optimistic id it replaces so
        // normalize keeps the turn's stableId constant across the swap.
        let stableMap = state.messageStableIdMap;
        if (currentTurnOptimisticId !== null && snapshotUserEcho) {
          var _state$messageStableI2;
          stableMap = new Map(state.messageStableIdMap);
          stableMap.set(snapshotUserEcho.id, (_state$messageStableI2 = state.messageStableIdMap.get(currentTurnOptimisticId)) !== null && _state$messageStableI2 !== void 0 ? _state$messageStableI2 : currentTurnOptimisticId);
        }
        if (state.turnId === null) {
          // No active turn — can't partition by turn, keep all leftover at tail.
          return Object.assign({}, state, {
            messages: [...ordered, ...notInSnapshot],
            messageStableIdMap: stableMap
          });
        }

        // Restore prior-turn messages before the active turn; keep in-flight
        // deltas at the tail so partial streamed text isn't dropped during a
        // mid-stream reconcile.
        const priorTurn = notInSnapshot.filter(m => !m.id.startsWith(DELTA_ID_PREFIX) && state.messageTurnIdMap.get(m.id) !== state.turnId);
        const inflightDeltas = notInSnapshot.filter(m => m.id.startsWith(DELTA_ID_PREFIX));
        const messages = [...priorTurn, ...ordered, ...inflightDeltas];
        const reconcileMap = new Map(state.messageTurnIdMap);
        // The superseded optimistic message is gone from `messages`; drop its
        // turn-id entry too so the map doesn't accumulate a stale key per
        // reconcile over a long session.
        if (currentTurnOptimisticId !== null) {
          reconcileMap.delete(currentTurnOptimisticId);
        }
        for (const msg of action.messages) {
          if (!reconcileMap.has(msg.id)) {
            reconcileMap.set(msg.id, state.turnId);
          }
        }
        return Object.assign({}, state, {
          messages,
          messageTurnIdMap: reconcileMap,
          messageStableIdMap: stableMap
        });
      }
    case 'MESSAGE_REMOVE_LAST_OPTIMISTIC':
      {
        var _lastAfterDelta$id;
        const hasDelta = state.messages.some(msg => msg.id.startsWith(DELTA_ID_PREFIX));
        const cleanedMap = new Map(state.messageTurnIdMap);
        const cleaned = [];
        for (const msg of state.messages) {
          if (msg.id.startsWith(DELTA_ID_PREFIX)) {
            cleanedMap.delete(msg.id);
          } else {
            cleaned.push(msg);
          }
        }
        const lastAfterDelta = cleaned[cleaned.length - 1];
        const hasOptimistic = lastAfterDelta === null || lastAfterDelta === void 0 || (_lastAfterDelta$id = lastAfterDelta.id) === null || _lastAfterDelta$id === void 0 ? void 0 : _lastAfterDelta$id.startsWith(OPTIMISTIC_ID_PREFIX);
        if (!hasOptimistic && !hasDelta) return state;
        if (hasOptimistic) {
          cleanedMap.delete(lastAfterDelta.id);
          cleaned.pop();
        }
        return Object.assign({}, state, {
          messages: cleaned,
          messageTurnIdMap: cleanedMap
        });
      }
    case 'RESET':
      return Object.assign({}, state, {
        messages: [],
        messageTurnIdMap: new Map(),
        messageStableIdMap: new Map()
      });
    default:
      return state;
  }
}